import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { OrganizationService } from '../../services/organization.service';
import { PropertyService } from '../../services/property.service';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.scss']
})
export class OrganizationSettingsComponent implements OnInit {
  selectedLogoFile: File;
  expenseCategoriesText = '';

  attributes: any[] = [];
  attributeTypes: string[] = ['String', 'Number', 'Date'];
  newAttributeName = '';
  newAttributeType = 'String';
  attributeError: string = null;
  editForm = this.fb.group({
    id: [null],
    name: [null, Validators.required],
    contactName: [null],
    domain: [null, Validators.required],
    logoUrl: [null],
    marketingImageUrl: [null],
    address: [null],
    addressLine1: [null],
    addressLine2: [null],
    city: [null],
    state: [null],
    zipCode: [null],
    country: [null],
    bankName: [null],
    bankAccountNumber: [null],
    bankRoutingNumber: [null],
    zelle: [null],
    cashApp: [null],
    port: [null],
    secure: [null],
    expensePrompt: [null],
    defaultInvoiceDayOfMonth: [null],
    defaultInvoiceDueDayOfMonth: [null],
    defaultLateFeePercent: [null],
    defaultLateFeeFrequency: [null]
  });

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private propertyService: PropertyService,
    private router: Router
  ) {}

  ngOnInit() {
    this.organizationService.getOrganization().then((org) => {
      this.editForm.patchValue(org);
    });
    this.organizationService.getExpenseCategories().then((categories) => {
      if (Array.isArray(categories)) {
        this.expenseCategoriesText = categories.join('\n');
      }
    }).catch(() => {});
    this.loadAttributes();
  }

  loadAttributes() {
    this.propertyService.getAdminAttributes().then((attrs: any[]) => {
      this.attributes = Array.isArray(attrs) ? attrs : [];
    }).catch(() => {
      this.attributes = [];
    });
  }

  addAttribute() {
    this.attributeError = null;
    const name = (this.newAttributeName || '').trim();
    if (!name) {
      this.attributeError = 'Attribute name is required.';
      return;
    }
    this.propertyService.createAttribute({ name, attributeType: this.newAttributeType }).then(() => {
      this.newAttributeName = '';
      this.newAttributeType = 'String';
      this.loadAttributes();
    }).catch((err) => {
      this.attributeError = (err && err.error && err.error.error) || 'Failed to add attribute.';
    });
  }

  deleteAttribute(attr: any) {
    this.attributeError = null;
    if (!attr || !attr.id) {
      return;
    }
    if (!confirm(`Delete attribute "${attr.name}"?`)) {
      return;
    }
    this.propertyService.deleteAttribute(attr.id).then(() => {
      this.loadAttributes();
    }).catch((err) => {
      this.attributeError = (err && err.error && err.error.error) || 'Failed to delete attribute.';
    });
  }

  isSharedAttribute(attr: any): boolean {
    return !attr || !attr.organizationId;
  }

  save() {
    if (this.editForm.valid) {
      const org: any = this.editForm.value;
      org.expenseCategories = this.parseExpenseCategories();
      this.organizationService.updateOrganization(org).then(() => {
        this.router.navigate(['/admin-landing']);
      });
    }
  }

  private parseExpenseCategories(): string[] {
    return (this.expenseCategoriesText || '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  }

  close() {
    this.router.navigate(['/admin-landing']);
  }

  handleAddressChange(address: any) {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';
    let country = '';

    for (const comp of address.address_components) {
      if (comp.types.includes('street_number')) {
        streetNumber = comp.short_name;
      }
      if (comp.types.includes('route')) {
        route = comp.short_name;
      }
      if (comp.types.includes('locality')) {
        city = comp.short_name;
      }
      if (comp.types.includes('administrative_area_level_1')) {
        state = comp.short_name;
      }
      if (comp.types.includes('postal_code')) {
        zip = comp.short_name;
      }
      if (comp.types.includes('country')) {
        country = comp.short_name;
      }
    }

    const line1 = (streetNumber + ' ' + route).trim();

    this.editForm.patchValue({
      address: address.formatted_address,
      addressLine1: line1,
      city: city,
      state: state,
      zipCode: zip,
      country: country
    });
  }

  onLogoFileChanged(event: any) {
    this.selectedLogoFile = event.target.files[0];

    const reader = new FileReader();
    reader.readAsDataURL(this.selectedLogoFile);
    reader.onload = () => {
      this.editForm.patchValue({ logoUrl: reader.result });
    };

    const orgNameControl = this.editForm.get('name');
    const orgName = orgNameControl ? orgNameControl.value : null;
    if (orgName) {
      this.organizationService.uploadImage(orgName, this.selectedLogoFile).then(() => {});
    }
  }
}
